import { AlertCircle, RefreshCcw, Trophy, UsersRound } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "../../../components/skeleton";
import { apiFetch } from "../../../lib/api-client";
import { formatCurrency, formatMonthYear } from "../../../lib/i18n";

type StaffPerformanceStatus = "on_track" | "needs_attention" | "at_risk";

interface StaffPerformanceItem {
  staffId: string;
  staffName: string;
  rank: number;
  performanceScore: number;
  status: StaffPerformanceStatus;
  goals: {
    revenueCents: number | null;
    tasksCompleted: number | null;
    dealsWon: number | null;
  };
  metrics: {
    revenueCents: number;
    dealsWon: number;
    activeDeals: number;
    activePipelineCents: number;
    tasksCompleted: number;
    openTasks: number;
    overdueTasks: number;
    activitiesLogged: number;
  };
  attainment: {
    revenuePct: number | null;
    tasksPct: number | null;
    dealsPct: number | null;
    blendedPct: number | null;
  };
  coaching: string[];
}

interface TeamPerformanceData {
  period: string;
  generatedAt: string;
  team: {
    staffCount: number;
    revenueCents: number;
    dealsWon: number;
    tasksCompleted: number;
    overdueTasks: number;
    coachingSummary: string;
  };
  leaderboard: StaffPerformanceItem[];
}

const STATUS_LABELS: Record<StaffPerformanceStatus, string> = {
  on_track: "On track",
  needs_attention: "Needs attention",
  at_risk: "At risk",
};

const STATUS_CLASSES: Record<StaffPerformanceStatus, string> = {
  on_track: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  needs_attention: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  at_risk: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
};

function getCurrentMonthValue() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

function formatPeriodLabel(period: string) {
  const [year, month] = period.split("-");
  if (!year || !month) return period;
  return formatMonthYear(new Date(Date.UTC(Number(year), Number(month) - 1, 1)));
}

function buildDegradedTeamPerformanceData(period: string): TeamPerformanceData {
  return {
    period,
    generatedAt: new Date().toISOString(),
    team: {
      staffCount: 0,
      revenueCents: 0,
      dealsWon: 0,
      tasksCompleted: 0,
      overdueTasks: 0,
      coachingSummary: "Team performance is temporarily degraded; live CRM evidence will reappear when the read model is available.",
    },
    leaderboard: [],
  };
}

export default function TeamPerformancePage() {
  const [period, setPeriod] = useState(getCurrentMonthValue);
  const currentPeriod = useMemo(() => getCurrentMonthValue(), []);
  const [data, setData] = useState<TeamPerformanceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [degradedMessage, setDegradedMessage] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setDegradedMessage(null);

    try {
      const response = await apiFetch<{ data: TeamPerformanceData }>(
        `/crm/intelligence/team-performance?period=${encodeURIComponent(period)}`,
      );
      setData(response.data);
    } catch {
      setData(buildDegradedTeamPerformanceData(period));
      setDegradedMessage("Team performance is temporarily unavailable. The CRM workspace stays usable while the live metrics read model recovers.");
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const topRep = data?.leaderboard[0] ?? null;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Team Performance</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Per-rep metrics, coaching signals, activity tracking, and leaderboard evidence for {formatPeriodLabel(period)}.
          </p>
        </div>
        <div className="flex flex-col gap-3 rounded-xl border bg-[var(--color-card)] p-4 sm:flex-row sm:items-end">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--color-foreground)]">Performance period</span>
            <input
              aria-label="Performance period"
              type="month"
              value={period}
              max={currentPeriod}
              onChange={(event) => setPeriod(event.target.value)}
              className="h-10 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 text-sm text-[var(--color-foreground)]"
            />
          </label>
          <button
            type="button"
            onClick={() => setPeriod(currentPeriod)}
            disabled={period === currentPeriod}
            className="h-10 rounded-lg border border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            This month
          </button>
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] px-3 text-sm font-medium text-[var(--color-primary-foreground)]"
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : error ? (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="mr-2 inline h-4 w-4" />
          {error}
          <button type="button" onClick={fetchData} className="ml-3 underline">
            Retry
          </button>
        </div>
      ) : data ? (
        <>
          {degradedMessage ? (
            <div className="rounded-md border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/10 px-4 py-3 text-sm text-[var(--color-warning)]">
              <AlertCircle className="mr-2 inline h-4 w-4" />
              {degradedMessage}
              <button type="button" onClick={fetchData} className="ml-3 underline">
                Retry
              </button>
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-4">
            <MetricCard label="Staff tracked" value={String(data.team.staffCount)} />
            <MetricCard label="Won revenue" value={formatCurrency(data.team.revenueCents)} />
            <MetricCard label="Deals won" value={String(data.team.dealsWon)} />
            <MetricCard label="Tasks completed" value={String(data.team.tasksCompleted)} />
          </div>

          <div className="grid gap-4 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="rounded-xl border bg-[var(--color-card)] p-5">
              <div className="flex items-center gap-2">
                <UsersRound className="h-5 w-5 text-[var(--color-primary)]" />
                <h2 className="font-semibold text-[var(--color-foreground)]">Coaching summary</h2>
              </div>
              <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">{data.team.coachingSummary}</p>
              {topRep && (
                <div className="mt-5 rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-primary)]">
                    <Trophy className="h-4 w-4" />
                    Current leader
                  </div>
                  <div className="mt-2 text-xl font-bold text-[var(--color-foreground)]">{topRep.staffName}</div>
                  <p className="text-sm text-[var(--color-muted-foreground)]">
                    {topRep.performanceScore}/100 performance score from live CRM evidence.
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-xl border bg-[var(--color-card)]">
              <div className="border-b px-5 py-4">
                <h2 className="font-semibold text-[var(--color-foreground)]">Leaderboard</h2>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  Ranked by quota attainment, completed work, pipeline ownership, and overdue-task pressure.
                </p>
              </div>
              <div className="divide-y">
                {data.leaderboard.length > 0 ? data.leaderboard.map((rep) => (
                  <RepRow key={rep.staffId} rep={rep} />
                )) : (
                  <div className="px-5 py-8 text-sm text-[var(--color-muted-foreground)]">
                    No staff activity, ownership, or goals found for {formatPeriodLabel(period)}.
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-[var(--color-card)] p-4">
      <div className="text-sm text-[var(--color-muted-foreground)]">{label}</div>
      <div className="mt-1 text-2xl font-bold text-[var(--color-foreground)]">{value}</div>
    </div>
  );
}

function RepRow({ rep }: { rep: StaffPerformanceItem }) {
  return (
    <div className="space-y-3 px-5 py-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs font-semibold text-[var(--color-foreground)]">
              #{rep.rank}
            </span>
            <span className="font-semibold text-[var(--color-foreground)]">{rep.staffName}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[rep.status]}`}>
              {STATUS_LABELS[rep.status]}
            </span>
          </div>
          <div className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            {rep.performanceScore}/100 score · {rep.metrics.activitiesLogged} activities · {rep.metrics.openTasks} open tasks
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-sm text-[var(--color-foreground)]">
          <SmallMetric label="Revenue" value={formatCurrency(rep.metrics.revenueCents)} />
          <SmallMetric label="Deals" value={String(rep.metrics.dealsWon)} />
          <SmallMetric label="Tasks" value={String(rep.metrics.tasksCompleted)} />
        </div>
      </div>
      <div className="grid gap-3 text-xs text-[var(--color-muted-foreground)] md:grid-cols-3">
        <span>Pipeline: {formatCurrency(rep.metrics.activePipelineCents)} across {rep.metrics.activeDeals} active deals</span>
        <span>Revenue attainment: {rep.attainment.revenuePct ?? 0}%</span>
        <span>Overdue tasks: {rep.metrics.overdueTasks}</span>
      </div>
      <ul className="space-y-1 text-sm text-[var(--color-muted-foreground)]">
        {rep.coaching.map((item) => (
          <li key={item}>- {item}</li>
        ))}
      </ul>
    </div>
  );
}

function SmallMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[var(--color-muted-foreground)]">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
